import { Avatar, Box, Button, Checkbox, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { arrayToggle } from '../../../common/utils/common.utils';
import { currentContextAtom } from '../../../state';
import { ContactsDraft } from '../state';
import { CompanyRoleAssignment } from '../../../common/models/user.models';
import { BaseFormProps } from '../FormDrawer';
import PhotosUtils from '../../../common/utils/photos.utils';

export interface ContactsFormProps extends BaseFormProps {
  contacts: CompanyRoleAssignment[];
  onSubmit: (contactIds: string[]) => void;
  description: string;
}

export default function ContactsForm({ contacts, description, onSubmit, onChange, onCancel }: ContactsFormProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'settings']);
  const { companyRoleAssignments } = useAtomValue(currentContextAtom);
  const { handleSubmit, formState: { isDirty }, setValue, watch } = useForm<ContactsDraft>({
    defaultValues: {
      contactIds: contacts.map(({ userId }) => userId),
    },
  });
  const contactIds = watch('contactIds');
  const values = watch();

  const handleContactIdsChange = (id: string): void => {
    const newContactIds = [...contactIds];

    arrayToggle(newContactIds, id);

    setValue('contactIds', newContactIds, { shouldDirty: true });
  };

  useEffect((): void => {
    onChange(isDirty);
  }, [values, isDirty]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit((data) => onSubmit(data.contactIds))}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="titleLarge">
          {t('settings:contact_list')}
        </Typography>
        <Typography variant="bodyMedium" color="secondary">
          {t(description)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {[...companyRoleAssignments]
          .sort((roleA, roleB) => roleA.user.first_name.localeCompare(roleB.user.first_name))
          .map(({ userId, user: { first_name, last_name, auth_provider_avatar_url, default_avatar, email } }) => (
            <Box
              key={userId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1,
                gap: 1,
              }}
            >
              <Checkbox
                checked={contactIds.includes(userId)}
                onChange={() => handleContactIdsChange(userId)}
                disableRipple
              />
              <Avatar src={PhotosUtils.getUserAvatarPicture({ auth_provider_avatar_url, default_avatar })} size={40} />
              <Box>
                <Typography variant="bodyLarge">
                  {first_name} {last_name}
                </Typography>
                <Typography variant="bodyMedium" color="secondary">{email}</Typography>
              </Box>
            </Box>
          ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, position: 'sticky', bottom: -24, backgroundColor: 'white', py: 1 }}>
        <Button variant="text" onClick={onCancel}>
          {t('common:cancel')}
        </Button>
        <Button variant="contained" type="submit" disabled={!isDirty}>
          {t('common:validate')}
        </Button>
      </Box>
    </Box>
  );
}
